import { useContext, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { alpha } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InventoryIcon from '@mui/icons-material/Inventory';
import PercentIcon from '@mui/icons-material/Percent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import axiosInstance from '../../lib/axios';
import { getUser } from '../../utils/auth';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface ProfitSummary {
  period: {
    startDate: string;
    endDate: string;
    type: string;
  };
  revenue: number;
  cogs: number;
  grossProfit: number;
  grossMarginPercent: number;
  salesCount: number;
}

interface PeriodProfitData {
  period: string;
  revenue: number;
  cogs: number;
  profit: number;
  marginPercent: number;
}

interface TopProfitProduct {
  productId: string;
  productName: string;
  quantitySold: number;
  revenue: number;
  cogs: number;
  profit: number;
  marginPercent: number;
}

interface SpendingData {
  totalSpending: number;
  restockCount: number;
  bySupplier: {
    supplierId: string;
    supplierName: string;
    totalSpent: number;
    restockCount: number;
  }[];
  byPeriod: {
    period: string;
    spending: number;
    restockCount: number;
  }[];
}

interface ExpenseAnalyticsSummary {
  totalAmount: number;
  expenseCount: number;
  averageAmount: number;
  byCategory: Record<string, number>;
  byPaymentMethod: Record<string, number>;
}

const fetchProfitAnalytics = async (
  startDate: Date,
  endDate: Date,
  periodType: string
): Promise<ProfitSummary> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    periodType,
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/sales/analytics/profit', { params });
  return response.data;
};

const fetchProfitByPeriod = async (
  startDate: Date,
  endDate: Date,
  periodType: string
): Promise<PeriodProfitData[]> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    periodType,
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/sales/analytics/profit/by-period', { params });
  return response.data.data; // Unwrap nested data array from { period, data } response
};

const fetchTopProfitProducts = async (
  startDate: Date,
  endDate: Date
): Promise<TopProfitProduct[]> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    limit: 10,
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/sales/analytics/top-profit-products', { params });
  return response.data.products; // Unwrap nested products array from { period, products } response
};

const fetchSpendingAnalytics = async (
  startDate: Date,
  endDate: Date
): Promise<SpendingData> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/inventory/analytics/spending', { params });
  return response.data;
};

const fetchExpenseAnalytics = async (
  startDate: Date,
  endDate: Date
): Promise<ExpenseAnalyticsSummary> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/expenses/analytics/summary', { params });
  return response.data;
};

const CHART_COLORS = [
  palette.primary,
  palette.success,
  palette.info,
  palette.accent,
  palette.warning,
  '#9C27B0',
  '#00BCD4',
  '#FF5722',
];

export const ProfitAnalyticsDashboard = () => {
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  // Date range state - default to last 30 days
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [selectedPreset, setSelectedPreset] = useState<string>('30d');

  // Determine period type based on date range
  const periodType = useMemo(() => {
    const days = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (days <= 31) return 'daily';
    if (days <= 90) return 'weekly';
    return 'monthly';
  }, [startDate, endDate]);

  const { data: profitSummary, isLoading: loadingSummary, error: summaryError } = useQuery({
    queryKey: ['profitAnalytics', startDate, endDate, periodType],
    queryFn: () => fetchProfitAnalytics(startDate, endDate, periodType),
  });

  const { data: profitByPeriod, isLoading: loadingPeriod, error: periodError } = useQuery({
    queryKey: ['profitByPeriod', startDate, endDate, periodType],
    queryFn: () => fetchProfitByPeriod(startDate, endDate, periodType),
  });

  const { data: topProducts, isLoading: loadingProducts, error: productsError } = useQuery({
    queryKey: ['topProfitProducts', startDate, endDate],
    queryFn: () => fetchTopProfitProducts(startDate, endDate),
  });

  const { data: spendingData, isLoading: loadingSpending, error: spendingError } = useQuery({
    queryKey: ['spendingAnalytics', startDate, endDate],
    queryFn: () => fetchSpendingAnalytics(startDate, endDate),
  });

  const { data: expenseData, isLoading: loadingExpenses, error: expenseError } = useQuery({
    queryKey: ['expenseAnalytics', startDate, endDate],
    queryFn: () => fetchExpenseAnalytics(startDate, endDate),
  });

  const handlePresetClick = (preset: string) => {
    const end = new Date();
    const start = new Date();

    switch (preset) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      case '1y':
        start.setFullYear(start.getFullYear() - 1);
        break;
    }

    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(preset);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const colors = mode === 'dark'
    ? {
        bg: palette.background,
        surface: palette.surface,
        surfaceElevated: palette.surfaceElevated,
        border: palette.border,
        borderLight: palette.borderLight,
        textPrimary: palette.textPrimary,
        textSecondary: palette.textSecondary,
        textMuted: palette.textMuted,
      }
    : {
        bg: palette.light.background,
        surface: palette.light.surface,
        surfaceElevated: palette.light.surfaceElevated,
        border: palette.light.border,
        borderLight: palette.light.borderLight,
        textPrimary: palette.light.textPrimary,
        textSecondary: palette.light.textSecondary,
        textMuted: palette.light.textMuted,
      };

  const cardStyles = {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: theme.radius.xl,
    p: 3,
    transition: theme.transitions.normal,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-DZ', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getDaysRange = () => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const isLoading = loadingSummary || loadingPeriod || loadingProducts || loadingSpending || loadingExpenses;
  const error = summaryError || periodError || productsError || spendingError || expenseError;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          sx={{
            borderRadius: theme.radius.lg,
            backgroundColor: palette.errorMuted,
            color: palette.error,
          }}
        >
          {error instanceof Error ? error.message : 'Failed to load analytics data'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, ...animations.slideDown }}>
        <Typography
          variant="overline"
          sx={{
            color: palette.primary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.widest,
            display: 'block',
            mb: 0.5,
          }}
        >
          Rentabilite
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            mb: 0.5,
          }}
        >
          Profit Analytics
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {getDaysRange()} days of profitability insights
        </Typography>
      </Box>

      {/* Date Range Picker */}
      <Box
        sx={{
          ...cardStyles,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
            }}
          >
            Time Period
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap' }}>
          <ButtonGroup
            variant="outlined"
            sx={{
              '& .MuiButton-root': {
                borderColor: colors.border,
                color: colors.textSecondary,
                textTransform: 'none',
                fontWeight: typography.fontWeight.medium,
                fontSize: typography.fontSize.sm,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: palette.primaryMuted,
                },
              },
            }}
          >
            {['7d', '30d', '90d', '1y'].map((preset) => (
              <Button
                key={preset}
                onClick={() => handlePresetClick(preset)}
                sx={{
                  ...(selectedPreset === preset && {
                    backgroundColor: palette.primaryMuted,
                    borderColor: `${palette.primary} !important`,
                    color: palette.primary,
                  }),
                }}
              >
                {preset === '7d' && 'Last 7 days'}
                {preset === '30d' && 'Last 30 days'}
                {preset === '90d' && 'Last 90 days'}
                {preset === '1y' && 'Last year'}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              type="date"
              label="Start Date"
              value={formatDateForInput(startDate)}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              to
            </Typography>
            <TextField
              type="date"
              label="End Date"
              value={formatDateForInput(endDate)}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card
            sx={{
              ...cardStyles,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.primary,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  Revenue
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: colors.textPrimary,
                    mt: 1,
                  }}
                >
                  {formatCurrency(profitSummary?.revenue || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {profitSummary?.salesCount || 0} sales
                </Typography>
              </Box>
              <ShoppingCartIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            sx={{
              ...cardStyles,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.warning,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  Cost of Goods
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.warning,
                    mt: 1,
                  }}
                >
                  {formatCurrency(profitSummary?.cogs || 0)}
                </Typography>
              </Box>
              <InventoryIcon sx={{ fontSize: 40, color: alpha(palette.warning, 0.2) }} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            sx={{
              ...cardStyles,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor:
                  (profitSummary?.grossProfit || 0) >= 0 ? palette.success : palette.error,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  Gross Profit
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color:
                      (profitSummary?.grossProfit || 0) >= 0 ? palette.success : palette.error,
                    mt: 1,
                  }}
                >
                  {(profitSummary?.grossProfit || 0) > 0 ? '+' : ''}
                  {formatCurrency(profitSummary?.grossProfit || 0)}
                </Typography>
              </Box>
              {(profitSummary?.grossProfit || 0) >= 0 ? (
                <TrendingUpIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 40, color: alpha(palette.error, 0.2) }} />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            sx={{
              ...cardStyles,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.info,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  Gross Margin
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.info,
                    mt: 1,
                  }}
                >
                  {(profitSummary?.grossMarginPercent || 0).toFixed(1)}%
                </Typography>
              </Box>
              <PercentIcon sx={{ fontSize: 40, color: alpha(palette.info, 0.2) }} />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Profit Trend Chart */}
        <Grid item xs={12} lg={8}>
          <Box sx={cardStyles}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Profit Trend
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart
                data={profitByPeriod || []}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={palette.success} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={palette.success} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorRevenue2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={palette.primary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={palette.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorCogs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={palette.warning} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={palette.warning} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis
                  dataKey="period"
                  stroke={colors.textMuted}
                  tickFormatter={formatDate}
                  style={{ fontSize: typography.fontSize.xs }}
                />
                <YAxis stroke={colors.textMuted} style={{ fontSize: typography.fontSize.xs }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: theme.radius.md,
                    color: colors.textPrimary,
                  }}
                  labelStyle={{ color: colors.textPrimary }}
                  formatter={(value: any, name: string) => {
                    if (name === 'revenue') return [formatCurrency(value), 'Revenue'];
                    if (name === 'cogs') return [formatCurrency(value), 'COGS'];
                    if (name === 'profit') return [formatCurrency(value), 'Profit'];
                    return [value, name];
                  }}
                  labelFormatter={(label) => formatDate(label)}
                />
                <Legend
                  wrapperStyle={{ color: colors.textPrimary }}
                  formatter={(value) => {
                    if (value === 'revenue') return 'Revenue';
                    if (value === 'cogs') return 'COGS';
                    if (value === 'profit') return 'Profit';
                    return value;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={palette.primary}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRevenue2)"
                />
                <Area
                  type="monotone"
                  dataKey="cogs"
                  stroke={palette.warning}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorCogs)"
                />
                <Area
                  type="monotone"
                  dataKey="profit"
                  stroke={palette.success}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorProfit)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        {/* Spending by Supplier Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Box sx={cardStyles}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Spending by Supplier
            </Typography>
            {spendingData?.bySupplier && spendingData.bySupplier.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={spendingData.bySupplier}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      paddingAngle={2}
                      dataKey="totalSpent"
                      nameKey="supplierName"
                    >
                      {spendingData.bySupplier.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: colors.surface,
                        border: `1px solid ${colors.border}`,
                        borderRadius: theme.radius.md,
                        color: colors.textPrimary,
                      }}
                      formatter={(value: any) => [formatCurrency(value), 'Spent']}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textMuted,
                      mb: 1,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    Total: {formatCurrency(spendingData.totalSpending)}
                  </Typography>
                  {spendingData.bySupplier.slice(0, 4).map((supplier, index) => (
                    <Box
                      key={supplier.supplierId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textSecondary,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {supplier.supplierName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textPrimary,
                          fontFamily: typography.fontFamily.mono,
                        }}
                      >
                        {formatCurrency(supplier.totalSpent)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 250,
                }}
              >
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  No spending data available
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Spending Summary Card */}
      {spendingData && (
        <Box sx={{ ...cardStyles, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AccountBalanceWalletIcon sx={{ color: palette.warning }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
              }}
            >
              Spending Summary
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 0.5 }}>
                Total Spending
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  color: palette.warning,
                }}
              >
                {formatCurrency(spendingData.totalSpending)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 0.5 }}>
                Restock Count
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                }}
              >
                {spendingData.restockCount}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 0.5 }}>
                Suppliers Used
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                }}
              >
                {spendingData.bySupplier?.length || 0}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 0.5 }}>
                Avg per Restock
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                }}
              >
                {spendingData.restockCount > 0
                  ? formatCurrency(spendingData.totalSpending / spendingData.restockCount)
                  : formatCurrency(0)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Expense Summary Card */}
      {expenseData && (
        <Box sx={{ ...cardStyles, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AccountBalanceWalletIcon sx={{ color: palette.error }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
              }}
            >
              Expenses Summary
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 0.5 }}>
                Total Expenses
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  color: palette.error,
                }}
              >
                {formatCurrency(expenseData.totalAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 0.5 }}>
                Expense Count
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                }}
              >
                {expenseData.expenseCount}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 0.5 }}>
                Average Expense
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                }}
              >
                {formatCurrency(expenseData.averageAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 0.5 }}>
                Net After Expenses
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  color: (profitSummary?.grossProfit || 0) - expenseData.totalAmount >= 0
                    ? palette.success
                    : palette.error,
                }}
              >
                {formatCurrency((profitSummary?.grossProfit || 0) - expenseData.totalAmount)}
              </Typography>
            </Grid>
          </Grid>
          {/* Expense breakdown by category */}
          {expenseData.byCategory && Object.keys(expenseData.byCategory).length > 0 && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: colors.textMuted,
                  mb: 1,
                  fontWeight: typography.fontWeight.semibold,
                }}
              >
                By Category
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(expenseData.byCategory).map(([category, amount], index) => (
                  <Box
                    key={category}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      backgroundColor: colors.surfaceElevated,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: theme.radius.md,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.textSecondary, textTransform: 'capitalize' }}
                    >
                      {category.replace('_', ' ')}:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textPrimary,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.semibold,
                      }}
                    >
                      {formatCurrency(amount as number)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Top Profitable Products Table */}
      <Box sx={cardStyles}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
            mb: 3,
          }}
        >
          Top Profitable Products
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                >
                  Rank
                </TableCell>
                <TableCell
                  sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                >
                  Product
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                >
                  Qty Sold
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                >
                  Revenue
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                >
                  Profit
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                >
                  Margin
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topProducts && topProducts.length > 0 ? (
                topProducts.map((product, index) => (
                  <TableRow
                    key={product.productId}
                    sx={{
                      '&:hover': { backgroundColor: colors.surfaceElevated },
                      transition: theme.transitions.fast,
                    }}
                  >
                    <TableCell sx={{ color: colors.textPrimary }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor:
                            index < 3 ? palette.successMuted : colors.surfaceElevated,
                          color: index < 3 ? palette.success : colors.textMuted,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      >
                        {index + 1}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: colors.textPrimary,
                        fontWeight: typography.fontWeight.medium,
                      }}
                    >
                      {product.productName}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: colors.textPrimary, fontFamily: typography.fontFamily.mono }}
                    >
                      {product.quantitySold}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: palette.primary,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.semibold,
                      }}
                    >
                      {formatCurrency(product.revenue)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: product.profit >= 0 ? palette.success : palette.error,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.semibold,
                      }}
                    >
                      {product.profit > 0 ? '+' : ''}
                      {formatCurrency(product.profit)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: colors.textSecondary,
                        fontFamily: typography.fontFamily.mono,
                      }}
                    >
                      {product.marginPercent.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: colors.textMuted }}>
                    No profit data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
